import {environment} from '../environments/environment';
import {NgModule, ErrorHandler, provideAppInitializer} from '@angular/core';
import {includeBearerTokenInterceptor} from 'keycloak-angular';
import {customProvideKeycloak, initializer} from './utils/app-init';
import {CommonModule} from '@angular/common';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {provideHttpClient, withInterceptors} from '@angular/common/http';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {LoginRedirectComponent} from './login-redirect/login-redirect.component';
import {SiteService, BootstrapService, ConfigService, ImageProcessingService, LicenseService, CalendarUtilsService, SharedEquipmentService, TicketsService, UserPreferenceService} from '@shared/services';
import {SharedDirectivesModule, SharedPipesModule} from './shared';
import {SharedComponentsModule} from '@shared/components/shared-components.module';
import {NgbModule, NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {LoggedInUserState} from '@shared/services/api/user/logged-in-user-state';
import {UserProfileService} from '@shared/services/api/user-profile.service';
import {RulesService} from './layout/rules/rulesmanager/rules.service';
import {MeterProvidersService} from './layout/sitemanager/meter-providers/meter-providers.service';
import {GoogleMapsService} from '@shared/services/google-maps/google-maps.service';
import {MetadataUnitService} from '@shared/services/api/metadata-unit.service';
import {MetadataTypeService} from '@shared/services/api/metadata-type.service';
import {MetadataService} from '@shared/services/api/metadata.service';
import {VacerFilesService} from '@app/layout/sitemanager/vacers/vacer-files/vacer-files.service';
import {CollectorEquipmentUpsertService} from './layout/sitemanager/collectors-equipment-upsert/collectors-equipment-upsert.service';
import {FavouritesService} from './layout/sitemanager/collectors-favourites/favourites.service';
import {RoutingStateService} from '@shared/services/routing/routing-state.service';
import {SegmentModule} from 'ngx-segment-analytics';
import {UsageAnalyticsService} from '@shared/services/usage-analytics/usage-analytics.service';
import {Observable, from} from 'rxjs';
import {LoggerModule} from 'ngx-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {MyBuildingsConfigurationService} from '@shared/services/api/integration-config/my-buildings-configuration.service';
import {FontAwesomeModule, FaIconLibrary} from '@fortawesome/angular-fontawesome';
import {fas} from '@fortawesome/free-solid-svg-icons';
import {far} from '@fortawesome/free-regular-svg-icons';
import {fab} from '@fortawesome/free-brands-svg-icons';

import {ChartService} from './layout/charts/chart.service';
import {BacerDevicesService} from './layout/sitemanager/bacers/bacer-devices/bacer-devices.service';
import {CollectorEquipmentCommissioningService} from './layout/sitemanager/collector-equipment-commissioning/collector-equipment-commissioning.service';
import {CollectorEquipmentCloneService} from './layout/sitemanager/collector-equipment-clone/collector-equipment-clone.service';
import {BacerScanActionsService} from '@shared/services/bacer-scan-actions/bacer-scan-actions.service';
import {MeterProviderService} from '@shared/services/api/meter-provider.service';
import {UnitUtilsService} from '@shared/services/util/unit-utils.service';
import {CustomErrorHandler} from './custom-error-handler';
import {PeriodUtilService} from '@shared/services/util/period-util.service';
import {ClassTransformer} from 'class-transformer';
import {TreeModule} from '@ali-hm/angular-tree-component';
import {ClientService} from '@shared/services/api/client.service';
import {LightboxModule} from 'ngx-lightbox';
import {AngularSvgIconModule} from 'angular-svg-icon';

export class WebpackTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<any> {
    return from(import(`../assets/i18n/${lang}.json`).then((m) => m.default));
  }
}

@NgModule({
  imports: [
    FontAwesomeModule,
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    LoggerModule.forRoot(AceLoggerService.getDefaultConfig()),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: WebpackTranslateLoader
      }
    }),
    AppRoutingModule,
    SharedDirectivesModule,
    SharedPipesModule,
    SharedComponentsModule,
    NgbModule,
    TreeModule,
    SegmentModule.forRoot({
      debug: !environment.production,
      loadOnInitialization: false
    }),
    LightboxModule,
    AngularSvgIconModule.forRoot()
  ],
  declarations: [AppComponent, LoginRedirectComponent],
  providers: [
    AceLoggerService,
    RoutingStateService,
    SharedEquipmentService,
    SiteService,
    ConfigService,
    LoggedInUserState,
    UserProfileService,
    UserPreferenceService,
    GoogleMapsService,
    ClientService,
    RulesService,
    MeterProvidersService,
    MetadataUnitService,
    MetadataTypeService,
    MetadataService,
    VacerFilesService,
    CollectorEquipmentUpsertService,
    CollectorEquipmentCommissioningService,
    CollectorEquipmentCloneService,
    FavouritesService,
    ImageProcessingService,
    MyBuildingsConfigurationService,
    UsageAnalyticsService,
    ChartService,
    TicketsService,
    LicenseService,
    CalendarUtilsService,
    UnitUtilsService,
    BootstrapService,
    BacerDevicesService,
    BacerScanActionsService,
    MeterProviderService,
    PeriodUtilService,
    NgbActiveModal,
    ClassTransformer,
    provideAppInitializer(initializer),
    customProvideKeycloak,
    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler
    },
    provideHttpClient(withInterceptors([includeBearerTokenInterceptor]))
  ],
  bootstrap: [AppComponent]
})
export class AppModule {

  constructor(library: FaIconLibrary) {
    library.addIconPacks(fas);
    library.addIconPacks(far);
    library.addIconPacks(fab);
  }
}
